import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import {
    CalendarClock,
    Clock,
    MapPin,
    NotebookPen,
    Video,
    X,
} from "lucide-react";
import {
    ScheduledSession,
    ScheduledStatus,
    SessionUbication,
} from "../../domain/types";
import { formatBookingTime } from "../../domain/formatting";
import type { WhatsAppReminderAction } from "../../domain/whatsappReminder";
import WhatsAppIcon from "../../../../shared/ui/icons/WhatsAppIcon";

interface ScheduledSessionCardProps {
    session: ScheduledSession;
    timezone: string | null;
    patientName?: string;
    spaceName?: string;
    onCancel: (session: ScheduledSession) => void;
    isCancelling?: boolean;
    /** Moving the session to another time. Omitted where it is not offered. */
    onReschedule?: (session: ScheduledSession) => void;
    isRescheduling?: boolean;
    /** Whether the session has already finished, in the therapist's timezone. */
    isPast?: boolean;
    /**
     * The WhatsApp button: the patient's chat with a reminder already written,
     * or a patient who does not allow WhatsApp. Omitted when there is no one
     * to write to.
     */
    whatsappReminder?: WhatsAppReminderAction;
    /** Pressed on a patient who switched WhatsApp off: warn the therapist. */
}

const STATUS_STYLES: Record<
    ScheduledStatus,
    { stripe: string; bg: string; text: string }
> = {
    [ScheduledStatus.SCHEDULED]: {
        stripe: "bg-brand-500",
        bg: "bg-brand-50/50 dark:bg-brand-900/10",
        text: "text-brand-700 dark:text-brand-400",
    },
    [ScheduledStatus.COMPLETED]: {
        stripe: "bg-green-500",
        bg: "bg-green-50/50 dark:bg-green-900/10",
        text: "text-green-700 dark:text-green-400",
    },
    [ScheduledStatus.CANCELLED]: {
        stripe: "bg-gray-300 dark:bg-gray-600",
        bg: "",
        text: "text-content-tertiary",
    },
};

/**
 * The same tint the week grid gives a finished session, so a list and a grid
 * of the same day read alike. Cancelled is already muted, and stays as it is.
 */
const PAST_STATUS_STYLES: Record<
    ScheduledStatus,
    { stripe: string; bg: string; text: string }
> = {
    [ScheduledStatus.SCHEDULED]: {
        stripe: "bg-brand-200 dark:bg-brand-500/40",
        bg: "bg-brand-50/30 dark:bg-brand-900/5",
        text: "text-brand-600/70 dark:text-brand-400/70",
    },
    [ScheduledStatus.COMPLETED]: {
        stripe: "bg-green-200 dark:bg-green-500/40",
        bg: "bg-green-50/30 dark:bg-green-900/5",
        text: "text-green-600/70 dark:text-green-400/70",
    },
    [ScheduledStatus.CANCELLED]: STATUS_STYLES[ScheduledStatus.CANCELLED],
};

/**
 * `session.date` and `session.hour` are already stored in the therapist's
 * timezone, so they are shown verbatim; the zone is displayed alongside so the
 * reading is never ambiguous.
 */
export default function ScheduledSessionCard({
    session,
    timezone,
    patientName,
    spaceName,
    onCancel,
    isCancelling = false,
    onReschedule,
    isRescheduling = false,
    isPast = false,
    whatsappReminder,
}: ScheduledSessionCardProps) {
    const { t } = useTranslation();
    const cancelled = session.scheduled_status === ScheduledStatus.CANCELLED;
    const palette = isPast ? PAST_STATUS_STYLES : STATUS_STYLES;
    const { stripe, bg } =
        palette[session.scheduled_status] ??
        palette[ScheduledStatus.SCHEDULED];
    const isOnline = session.ubication === SessionUbication.ONLINE;

    return (
        <article
            data-past={isPast || undefined}
            className={`group flex overflow-hidden rounded-xl border transition-all hover:shadow-md ${
                cancelled
                    ? "border-gray-200 opacity-60 dark:border-gray-800"
                    : `${isPast ? "border-gray-200/70 dark:border-gray-700/60" : "border-gray-200 dark:border-gray-700"} ${bg}`
            }`}
        >
            {/* Status colour stripe — the visual anchor borrowed from Google Calendar */}
            <div className={`w-1 shrink-0 ${stripe}`} aria-hidden="true" />

            {/* Body — on a phone the actions go under the text rather than
                beside it, so neither squeezes the other off the card. */}
            <div className="flex min-w-0 flex-1 flex-col gap-2 px-3 py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                <div className="flex min-w-0 flex-col gap-1">
                    {/* Time + duration */}
                    <p className="flex items-center gap-1 text-xs text-content-tertiary">
                        <Clock
                            size={11}
                            aria-hidden="true"
                            className="shrink-0"
                        />
                        <span>
                            {formatBookingTime(
                                session.date.slice(0, 10),
                                session.hour,
                                timezone
                            )}{" "}
                            · {session.duration}{" "}
                            {t("scheduler.common.minutes")}
                        </span>
                    </p>

                    {/* Patient / title */}
                    <h4
                        className={`truncate text-sm font-semibold leading-snug ${
                            cancelled
                                ? "text-content-tertiary line-through dark:text-gray-500"
                                : isPast
                                  ? "text-content-tertiary"
                                  : "text-content-primary"
                        }`}
                    >
                        {patientName ?? session.title}
                    </h4>

                    {/* Location and non-scheduled status badge */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                        {spaceName && (
                            <span className="flex items-center gap-1 text-xs text-content-tertiary">
                                <MapPin size={11} aria-hidden="true" />
                                {spaceName}
                            </span>
                        )}

                        {session.scheduled_status !==
                            ScheduledStatus.SCHEDULED && (
                            <span
                                className={`text-xs font-medium uppercase tracking-wide ${
                                    palette[session.scheduled_status]?.text ?? ""
                                }`}
                            >
                                {t(
                                    `scheduler.statuses.${session.scheduled_status}`
                                )}
                            </span>
                        )}
                    </div>
                </div>

                {/* A cancelled booking can be opened and rescheduled — held after
                    all, in a new slot — and nothing else. Past or not: a
                    session called off last week can still be held next week. */}
                {cancelled && (
                    <div className="-ml-2 flex shrink-0 items-center gap-1 sm:ml-0">
                        {session.session_id && (
                            <Link
                                to={`/session/${session.session_id}`}
                                aria-label={t("scheduler.schedule.viewThisSession", {
                                    subject: patientName ?? session.title,
                                })}
                                title={t("scheduler.schedule.viewSession")}
                                className="shrink-0 rounded-lg p-2 sm:p-1.5 text-brand-600 transition-colors hover:bg-brand-50 dark:hover:bg-brand-600/10"
                            >
                                <NotebookPen size={14} aria-hidden="true" />
                            </Link>
                        )}
                        {onReschedule && (
                            <button
                                type="button"
                                disabled={isRescheduling}
                                onClick={() => onReschedule(session)}
                                aria-label={t(
                                    "scheduler.schedule.rescheduleThisSession",
                                    { subject: patientName ?? session.title }
                                )}
                                title={t("scheduler.schedule.reschedule")}
                                className="shrink-0 rounded-lg p-2 sm:p-1.5 text-brand-600 transition-colors hover:bg-brand-50 disabled:opacity-50 dark:hover:bg-brand-600/10"
                            >
                                <CalendarClock size={14} aria-hidden="true" />
                            </button>
                        )}
                    </div>
                )}

                {/* Actions — reschedule and cancel wait for hover to keep the card
                    clean, but only where there is a pointer to hover with: a
                    touch screen shows them, or they could never be reached. */}
                {!cancelled && (
                    <div className="-ml-2 flex shrink-0 items-center gap-1 sm:ml-0">
                        {/* The meeting link as an icon, as on the week grid */}
                        {isOnline && session.meeting_link && (
                            <a
                                href={session.meeting_link}
                                target="_blank"
                                rel="noreferrer"
                                aria-label={t("scheduler.schedule.joinLink")}
                                title={t("scheduler.schedule.joinLink")}
                                className="shrink-0 rounded-lg p-2 sm:p-1.5 text-brand-600 transition-colors hover:bg-brand-50 dark:hover:bg-brand-600/10"
                            >
                                <Video size={14} aria-hidden="true" />
                            </a>
                        )}
                        {whatsappReminder?.kind === "open" && !isPast && (
                            <a
                                href={whatsappReminder.url}
                                target="_blank"
                                rel="noreferrer"
                                onClick={whatsappReminder.onOpen}
                                aria-label={t(
                                    "scheduler.schedule.whatsappReminderTo",
                                    { subject: patientName ?? session.title }
                                )}
                                title={t("scheduler.schedule.whatsappReminder")}
                                className="shrink-0 rounded-lg p-2 sm:p-1.5 text-brand-600 transition-colors hover:bg-brand-50 dark:hover:bg-brand-600/10"
                            >
                                <WhatsAppIcon className="size-3.5" />
                            </a>
                        )}
                        {session.session_id && (
                            <Link
                                to={`/session/${session.session_id}`}
                                aria-label={t("scheduler.schedule.viewThisSession", {
                                    subject: patientName ?? session.title,
                                })}
                                title={t("scheduler.schedule.viewSession")}
                                className="shrink-0 rounded-lg p-2 sm:p-1.5 text-brand-600 transition-colors hover:bg-brand-50 dark:hover:bg-brand-600/10"
                            >
                                <NotebookPen size={14} aria-hidden="true" />
                            </Link>
                        )}
                        {onReschedule && !isPast && (
                            <button
                                type="button"
                                disabled={isRescheduling}
                                onClick={() => onReschedule(session)}
                                aria-label={t(
                                    "scheduler.schedule.rescheduleThisSession",
                                    { subject: patientName ?? session.title }
                                )}
                                title={t("scheduler.schedule.reschedule")}
                                className="shrink-0 rounded-lg p-2 sm:p-1.5 text-content-tertiary transition-opacity sm:can-hover:opacity-0 sm:focus-visible:opacity-100 hover:bg-brand-50 hover:text-brand-600 sm:can-hover:group-hover:opacity-100 disabled:opacity-50 dark:hover:bg-brand-600/10"
                            >
                                <CalendarClock size={14} aria-hidden="true" />
                            </button>
                        )}
                        <button
                            type="button"
                            disabled={isCancelling}
                            onClick={() => onCancel(session)}
                            aria-label={t(
                                "scheduler.schedule.cancelThisSession",
                                { subject: patientName ?? session.title }
                            )}
                            title={t("scheduler.schedule.cancel")}
                            className="shrink-0 rounded-lg p-2 sm:p-1.5 text-content-tertiary transition-opacity sm:can-hover:opacity-0 sm:focus-visible:opacity-100 hover:bg-red-50 hover:text-red-500 sm:can-hover:group-hover:opacity-100 disabled:opacity-50 dark:hover:bg-red-500/10"
                        >
                            <X size={14} aria-hidden="true" />
                        </button>
                    </div>
                )}
            </div>
        </article>
    );
}
